import { Injectable } from '@angular/core'
import request from '../request'
import { Post, PostExtraCode } from '../types'
import { toUnderscore } from '../utils'

const contextToScopeMap = {
  queued: 'queued_and_awaiting_editing',
  purchased: 'posted_and_ordered',
  published: 'published',
  notPublished: 'not_published',
  declined: 'declined_and_ordered',
  queued_not_customer_preferred: 'queued_and_awaiting_editing_and_not_customer_preferred',
  with_last_transition_at: 'with_last_transition_at',
  withdrawn: 'withdrawn'
}

export type PostLoadContext = keyof typeof contextToScopeMap;

interface PostPaginationResult {
  posts: Post[];
  meta: {
    total: number
  };
}

@Injectable({providedIn: 'root'})
export class PostsService {

  getPostsByAccount(context: PostLoadContext, page: number = 1, perPage: number = null, queryParams?: Object): Promise<PostPaginationResult> {
    const scope = contextToScopeMap[context] || context
    const query = { scope: scope, page: page, per_page: perPage }
    if (queryParams) query['query_params'] = JSON.stringify(toUnderscore(queryParams))
    return request.get(`/api/account/posts.json`, query)
  }

  getPosts(customerId: number, context: PostLoadContext, page: number = 1, perPage: number = null, queryParams?: Object): Promise<PostPaginationResult> {
    const scope = contextToScopeMap[context] || context
    const query = { scope: scope, page: page, per_page: perPage }
    if (queryParams) query['query_params'] = JSON.stringify(toUnderscore(queryParams))
    return request.get(`/api/customers/${customerId}/posts.json`, query)
  }

  getPost(customerId: number, postId: number): Promise<Post> {
    return request.get(`/api/customers/${customerId}/posts/${postId}.json`)
  }

  sendToWriter(post: Post): Promise<any> {
    return request.put(`/api/customers/${post.customerId}/posts/${post.id}/request_editing.json`)
  }

  sortPosts(customerId: number, postIds: number[]): Promise<any> {
    return request.put(`/api/customers/${customerId}/posts/sort.json`, { postIds })
  }

  leaveReview(customerId: number, postId: number, comments: string): Promise<any> {
    return request.put(`/api/customers/${customerId}/posts/${postId}/leave_review.json`, { comments })
  }

  markViewed(customerId: number, postId: number): Promise<any> {
    return request.put(`/api/customers/${customerId}/posts/${postId}/mark_viewed.json`)
  }

  setPublished(customerId: number, postId: number, published: boolean): Promise<any> {
    return request.put(`/api/customers/${customerId}/posts/${postId}/set_published.json`, { published })
  }

  takeBack(customerId: number, postId: number): Promise<any> {
    return request.put(`/api/customers/${customerId}/posts/${postId}/take_back.json`)
  }

  redeliver(customerId: number, postId: number): Promise<any> {
    return request.post(`/api/customers/${customerId}/posts/${postId}/redeliver.json`)
  }

  declinePost(customerId: number, postId: number, comments: any, reasons: number[]): Promise<any> {
    return request.put(`/api/customers/${customerId}/posts/${postId}/decline.json`, {comments, reasons})
  }

  postNow(customerId: number, postId: number, paymentMethod: string, extraCodes: PostExtraCode[] = [], quantityFromCredits: number = null): Promise<any> {
    return request.put(`/api/customers/${customerId}/posts/${postId}/post_now.json`, toUnderscore({ paymentMethod, extraCodes, quantityFromCredits }))
  }

  pinPost(customerId: number, postId: number): Promise<any> {
    return request.put(`/api/customers/${customerId}/posts/${postId}/pin.json`)
  }

  unpinPost(customerId: number, postId: number): Promise<any> {
    return request.put(`/api/customers/${customerId}/posts/${postId}/unpin.json`)
  }

  toggleVisibility(customerId: number, postId: number): Promise<any> {
    return request.put(`/api/customers/${customerId}/posts/${postId}/toggle_wl_visibility.json`)
  }

  purchaseExtras(customerId: number, postId: number, extraCodes: PostExtraCode[] = []): Promise<Post> {
    return request.post(`/api/customers/${customerId}/posts/${postId}/purchase_extras.json`, toUnderscore({ extraCodes }))
  }

  getPostedCountForDateRange({customerId, startDate, endDate}: {customerId: number, startDate: Date, endDate: Date}): Promise<any> {
    const serializedStartDate = startDate.toISOString()
    const serializedEndDate = endDate.toISOString()
    return request.get(
      `/api/customers/${customerId}/posts/posted_count.json`,
      toUnderscore({
        startDate: serializedStartDate,
        endDate: serializedEndDate
      })
    )
  }

}
